import { useState, useEffect, useRef, RefObject } from 'react';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { replaceState } from 'client/helpers/routing';
import { NotificationProvider } from 'client/components/NotificationContext';
import theme from './theme';

type PathToMeta = {
  [key: string]: Record<string, any>;
};

const pathToMeta: PathToMeta = {
  '/': { component: 'public-home', requireAuth: false, loggedInComponent: 'home' },
  '/login': { component: 'login', requireAuth: false, loggedInRedirect: '/' },
  '/signup': { component: 'signup', requireAuth: false, loggedInRedirect: '/' },
  '/onboarding': { component: 'onboarding', requireAuth: true },
  '/forgot-password': { component: 'forgot-password', requireAuth: false },
  '/reset-password': { component: 'reset-password', requireAuth: false },
  '/check-email': { component: 'check-email', requireAuth: false },
  '/not-found': { component: 'not-found', requireAuth: false },
  '/error': { component: 'error', requireAuth: false },
  '/privacy-policy': { component: 'privacy-policy', requireAuth: false },
  '/terms-of-service': { component: 'terms-of-service', requireAuth: false },
  '/data-deletion': { component: 'data-deletion', requireAuth: false },
  '/styleguide': { component: 'styleguide', requireAuth: false },
  '/uploads': { component: 'uploads', requireAuth: true },
  '/my-posts': { component: 'my-posts', requireAuth: true },
  '/users/:username': { component: 'user-profile', requireAuth: false },
  '/users/:username/posts/:slugId': { component: 'post-detail', requireAuth: false },
  '/tags/:tagName': { component: 'tagged-posts', requireAuth: false },
  '/create-post': { component: 'create-post', requireAuth: true },
  '/edit-post/:slugId': { component: 'create-post', requireAuth: true },
  '/settings': { component: 'settings', requireAuth: true },
};

// Helper to match dynamic routes like /post/:id
function matchRoute(pathname: string): {
  meta: Record<string, any> | null;
  params: Record<string, string>;
} {
  // First try exact match
  if (pathToMeta[pathname]) {
    return { meta: pathToMeta[pathname], params: {} };
  }
  // Try dynamic routes
  for (const pattern of Object.keys(pathToMeta)) {
    if (!pattern.includes(':')) continue;
    const regex = new RegExp('^' + pattern.replace(/:([^/]+)/g, '([^/]+)') + '$');
    const match = pathname.match(regex);
    if (match) {
      const paramNames = (pattern.match(/:([^/]+)/g) || []).map((p) => p.slice(1));
      const params: Record<string, string> = {};
      paramNames.forEach((name, i) => {
        params[name] = match[i + 1];
      });
      return { meta: pathToMeta[pattern], params };
    }
  }
  return { meta: null, params: {} };
}

type PageComponentProps = {
  currentUserRef: RefObject<Record<string, any> | null>;
  loadCookieSession: () => Promise<void>;
  routeParams?: Record<string, string>;
  [key: string]: any;
};
type DynamicImport = {
  default: React.ComponentType<PageComponentProps>;
};

const App = () => {
  const [PageComponent, setPageComponent] =
    useState<React.ComponentType<PageComponentProps> | null>(null);
  const [routeParams, setRouteParams] = useState<Record<string, string>>({});
  const currentUserRef = useRef<Record<string, any> | null>(null);

  const isUserOnboarded = (user: Record<string, unknown> | null) => {
    return Boolean(user?.emailVerified && user?.username);
  };

  const onLocationChange = async () => {
    // Not found redirect
    let newPath = window.location.pathname;
    const { meta, params } = matchRoute(newPath);
    if (!meta) {
      replaceState('/not-found');
      return;
    }
    const { component, requireAuth, loggedInRedirect, loggedInComponent } = meta;
    const currentUser = currentUserRef.current;
    // If route requires authentication and user is not logged in, redirect to login
    if (requireAuth && !currentUser) {
      replaceState('/login');
      return;
    }
    // If user is logged in and not onboarded, redirect to onboarding
    if (currentUser && !isUserOnboarded(currentUser) && newPath !== '/onboarding') {
      replaceState('/onboarding');
      return;
    }
    // If user hits onboarding page and is onboarded, redirect to home
    if (newPath === '/onboarding' && isUserOnboarded(currentUser)) {
      replaceState('/');
      return;
    }
    // Redirect logged-in onboarded users away from login/signup pages
    if (loggedInRedirect && currentUser && isUserOnboarded(currentUser)) {
      replaceState(loggedInRedirect);
      return;
    }
    // Determine which component to load - use loggedInComponent if user is logged in and onboarded
    let componentToLoad = component;
    if (loggedInComponent && currentUser && isUserOnboarded(currentUser)) {
      componentToLoad = loggedInComponent;
    }
    const pageComponent = (await import(`./pages/${componentToLoad}/index.tsx`)) as DynamicImport;
    setRouteParams(params);
    setPageComponent(() => pageComponent.default ?? null);
  };

  async function loadCookieSession() {
    const sessionResponse = await fetch('/api/auth/session');
    const { user, session } = await sessionResponse.json();
    if (user && session) {
      currentUserRef.current = user;
    } else {
      currentUserRef.current = null;
    }
  }

  async function onInitialPageLoad() {
    await loadCookieSession();
    void onLocationChange();
  }

  useEffect(() => {
    const handlePopState = () => void onLocationChange();
    window.addEventListener('popstate', handlePopState);
    void onInitialPageLoad();
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <NotificationProvider>
          {PageComponent && (
            <PageComponent
              currentUserRef={currentUserRef}
              loadCookieSession={loadCookieSession}
              routeParams={routeParams}
            />
          )}
        </NotificationProvider>
      </ThemeProvider>
    </LocalizationProvider>
  );
};

export default App;
